import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Layout from '../Layout';
import {
  joindreOuNon, lireChiffrage, listerDommages, listerPostes,
  listerToutesLesPieces, lireSinistre, syntheseDuDossier, totalContenu,
} from '../api';

const euros = (v) =>
  v === null || v === undefined
    ? '—'
    : `${Number(v).toLocaleString('fr-BE', { minimumFractionDigits: 2,
        maximumFractionDigits: 2 })} €`;

const STATUTS = {
  CONFORME: { libelle: 'conforme', couleur: 'success' },
  A_DISCUTER: { libelle: 'à discuter', couleur: 'warning' },
  NON_CONFORME: { libelle: 'à contester', couleur: 'error' },
  INDETERMINE: { libelle: 'indéterminé', couleur: 'default' },
};

function Bloc({ titre, children }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }}>
      <Typography variant="h3" sx={{ mb: 2 }}>{titre}</Typography>
      {children}
    </Paper>
  );
}

/**
 * Dernière étape : ce que vaut le dossier, et ce qu'il lui manque.
 *
 * On distingue trois choses que rien ne doit mélanger : ce que l'assuré
 * réclame, ce que notre barème calcule, et ce qui reste à vérifier.
 */
export default function Dossier({ expertiseId, onRetour, onEtape }) {
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let vivant = true;
    Promise.all([
      lireSinistre(expertiseId),
      listerDommages(expertiseId),
      listerPostes(expertiseId).catch(() => []),
      lireChiffrage(expertiseId).catch(() => null),
      syntheseDuDossier(expertiseId).catch(() => null),
      totalContenu(expertiseId).catch(() => null),
      listerToutesLesPieces(expertiseId).catch(() => []),
    ])
      .then(([sinistre, dommages, postes, chiffrage, synthese, contenu,
              pieces]) => {
        if (vivant) setEtat({ sinistre, dommages, postes, chiffrage, synthese,
                              contenu, pieces });
      })
      .catch(() => vivant && setErreur('Impossible de charger votre dossier.'));
    return () => { vivant = false; };
  }, [expertiseId]);

  if (erreur) {
    return (
      <Layout etape="dossier" titre="Votre dossier" onEtape={onEtape}>
        <Alert severity="error">{erreur}</Alert>
      </Layout>
    );
  }
  if (!etat) {
    return (
      <Layout etape="dossier" titre="Votre dossier" onEtape={onEtape}><CircularProgress size={28} /></Layout>
    );
  }

  const { sinistre, dommages, postes, chiffrage, synthese, contenu,
          pieces } = etat;
  const contenuReclame = Number(contenu?.total_ask || 0);
  const fraisAnnexes = postes.filter((p) => !p.dommage && p.origin !== '1');
  const totalFrais = fraisAnnexes.reduce(
    (somme, p) => somme + Number(p.reclamer || 0), 0);
  const sansPhoto = dommages.filter((d) => !d.photos?.length);
  const sansChiffrage = dommages.filter(
    (d) => !postes.some((p) => p.dommage === d.id),
  );

  return (
    <Layout
      etape="dossier"
      onEtape={onEtape}
      titre="Votre dossier"
      introduction="Voici ce que votre dossier contient aujourd'hui, et ce qui le rendrait plus solide."
      actions={
        <>
          <Button onClick={onRetour}>Retour</Button>
          <Button variant="outlined" onClick={() => onEtape?.('aide')}>
            Besoin d'aide ?
          </Button>
          <Button variant="outlined" onClick={() => onEtape?.('suivi')}>
            Suivi
          </Button>
          <Button variant="outlined" onClick={() => onEtape?.('partage')}>
            Partager ce dossier
          </Button>
          <Button variant="contained" size="large"
                  onClick={() => onEtape?.('reclamation')}>
            Voir ma réclamation
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        <Bloc titre="Le chiffrage">
          {chiffrage ? (
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1">Ce que vous réclamez</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {euros(chiffrage.totalReclamerHtva)} HTVA
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" color="text.secondary">
                  Ce que le barème calcule
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {euros(chiffrage.totalCalculerHtva)} HTVA
                </Typography>
              </Stack>
              {Number(chiffrage.totalAccorderHtva) > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Proposition de l'assureur</Typography>
                  <Typography variant="body1">
                    {euros(chiffrage.totalAccorderHtva)} HTVA
                  </Typography>
                </Stack>
              )}
              {totalFrais > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">
                    Vos autres frais — relogement, honoraires
                  </Typography>
                  <Typography variant="body1">{euros(totalFrais)}</Typography>
                </Stack>
              )}
              {contenuReclame > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">
                    Votre contenu — mobilier et effets
                  </Typography>
                  <Typography variant="body1">
                    {euros(contenuReclame)}
                  </Typography>
                </Stack>
              )}
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Le montant calculé est un repère, pas une limite : vous restez
                libre de réclamer sur base du devis de votre entrepreneur.
                {sinistre.tva_recuperable === false &&
                  " Comme vous ne récupérez pas la TVA, votre indemnité se calculera TVA comprise."}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Rien n'est encore chiffré.
            </Typography>
          )}
        </Bloc>

        {synthese && synthese.total > 0 && (
          <Bloc titre="L'analyse de votre contrat">
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap
                   sx={{ mb: 2 }}>
              {Object.entries(synthese.par_statut)
                .filter(([, n]) => n > 0)
                .map(([statut, n]) => (
                  <Chip key={statut} color={STATUTS[statut]?.couleur || 'default'}
                        label={`${n} ${STATUTS[statut]?.libelle || statut}`} />
                ))}
            </Stack>
            {synthese.a_examiner > 0 && (
              <Typography variant="body1">
                {synthese.a_examiner} point{synthese.a_examiner > 1 ? 's' : ''} méritent
                votre attention, pour un enjeu de {euros(synthese.enjeu)}.
              </Typography>
            )}
            {synthese.non_valides > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {synthese.non_valides} analyse{synthese.non_valides > 1 ? 's' : ''} n'ont
                pas encore été relues par un humain.
              </Typography>
            )}
          </Bloc>
        )}

        {pieces?.length > 0 && (
          <Bloc titre="Vos pièces">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Décochez ce qui doit rester au dossier sans partir chez votre
              assureur : une photo de repérage, une note de travail.
            </Typography>
            <Stack spacing={0.5}>
              {pieces.map((p) => (
                <FormControlLabel
                  key={p.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={p.joindre !== false}
                      onChange={async (e) => {
                        const coche = e.target.checked;
                        setEtat((etatActuel) => ({
                          ...etatActuel,
                          pieces: etatActuel.pieces.map((x) =>
                            (x.id === p.id ? { ...x, joindre: coche } : x)),
                        }));
                        try {
                          await joindreOuNon(p.id, coche);
                        } catch {
                          setErreur("La pièce n'a pas pu être mise à jour.");
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {p.title || 'Sans titre'}
                      {p.category ? ` — ${p.category}` : ''}
                    </Typography>
                  }
                />
              ))}
            </Stack>
          </Bloc>
        )}

        <Bloc titre="Ce qui rendrait votre dossier plus solide">
          <Stack spacing={1.5}>
            {!sinistre.declaration && (
              <Alert severity="warning">Le récit du sinistre est vide.</Alert>
            )}
            {!sinistre.dateClaims && (
              <Alert severity="info">La date du sinistre n'est pas indiquée.</Alert>
            )}
            {dommages.length === 0 && (
              <Alert severity="warning">Aucun dommage n'est déclaré.</Alert>
            )}
            {sansPhoto.length > 0 && (
              <Alert severity="info">
                {sansPhoto.length} dommage{sansPhoto.length > 1 ? 's' : ''} sans
                photo : {sansPhoto.map((d) => d.element_libelle).join(', ')}.
                Une photo vaut mieux qu'une description.
              </Alert>
            )}
            {sansChiffrage.length > 0 && (
              <Alert severity="info">
                {sansChiffrage.length} dommage{sansChiffrage.length > 1 ? 's' : ''} ne
                sont pas encore chiffrés.
              </Alert>
            )}
            {sinistre.tva_recuperable === null && (
              <Alert severity="info">
                Vous n'avez pas indiqué si vous récupérez la TVA : cela change la
                base de votre indemnité.
              </Alert>
            )}
            {contenuReclame === 0 && (
              <Alert severity="info">
                Aucun objet n'est déclaré au contenu. Si le sinistre a touché
                du mobilier ou de l'électroménager, ajoutez-le : le bâtiment
                seul ne couvre pas ce que vous aviez à l'intérieur.
              </Alert>
            )}
            {!synthese?.total && (
              <Alert severity="info">
                Votre contrat n'a pas encore été analysé. Ajoutez-le à votre
                dossier pour savoir ce qu'il couvre.
              </Alert>
            )}
          </Stack>
        </Bloc>
      </Stack>
    </Layout>
  );
}
